import { Fragment, useMemo, useState } from 'react';
import type { PtoVolumePosition } from '@shared/api';
import { IconChevronDown, IconChevronRight } from '@shared/ui/icons';
import { visibleColumns, type VolumeCellContext } from './columns';
import { groupKeyOf, groupRows, type RowGroup } from './systems';
import {
  Facts,
  Grid,
  GroupTr,
  LinkButton,
  SectionTr,
  Toggle,
  ViewBar,
  WARN_SIGN,
  Warn,
} from './systemsUi';
import { VolumeRow } from './VolumeRow';

/** Групп не больше — раскрыты сразу; больше — свёрнуты (в ОВ2 76 систем). */
const OPEN_ALL_UP_TO = 6;

function rowsWord(n: number): string {
  const d = n % 10;
  const dd = n % 100;
  if (d === 1 && dd !== 11) return 'строка';
  if (d >= 2 && d <= 4 && (dd < 12 || dd > 14)) return 'строки';
  return 'строк';
}

function groupTitle(g: RowGroup<PtoVolumePosition>): string {
  switch (g.kind) {
    case 'system':
      return `Система ${g.label}`;
    case 'undefined':
      return 'Система не определена';
    case 'group':
      return g.label;
    default:
      return 'Без группы таблицы';
  }
}

interface Props {
  positions: PtoVolumePosition[];
  ctx: VolumeCellContext;
  /** Позиция, открытая по ссылке на строку: её группа раскрывается. */
  targetId: string | null;
}

/** Вид «По системам»: система ▶ раздел (часть РД) ▶ строки в порядке РД, с
 *  номерами строк. Метка нескольких систем («В25, В26») — одна группа; «не
 *  определена» — последней; у группы — число строк с сомнением разметки. В
 *  комплектах без систем — группы таблиц (`raw.group`). */
export function SystemsTable({ positions, ctx, targetId }: Props) {
  const columns = visibleColumns(ctx);
  // Признак разметки — всей ревизии (как у графы «Система»), а не показанных
  // строк: фильтр может оставить только позиции ИИ без `raw.system`.
  const revisionHasSystems = Boolean(ctx.hasSystems);
  const groups = useMemo(
    () => groupRows(positions, revisionHasSystems),
    [positions, revisionHasSystems],
  );
  const [open, setOpen] = useState<Set<string> | null>(null);
  const opened = open ?? new Set(groups.length <= OPEN_ALL_UP_TO ? groups.map((g) => g.key) : []);

  // Строка из ссылки — в раскрытой группе, в той же отрисовке: прокрутка
  // экрана (`PtoVolumes`) находит строку сразу. Один раз на строку и только
  // когда она в списке (фильтр «Только на проверку» экран снимает сам).
  const [shownTarget, setShownTarget] = useState<string | null>(null);
  const targetKey = targetId ? groupKeyOf(groups, targetId) : null;
  if (targetId && targetKey && shownTarget !== targetId) {
    setShownTarget(targetId);
    if (!opened.has(targetKey)) setOpen(new Set([...opened, targetKey]));
  }

  function toggle(key: string) {
    const next = new Set(opened);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setOpen(next);
  }

  const bySystems = groups.some((g) => g.kind === 'system' || g.kind === 'undefined');
  const defined = groups.filter((g) => g.kind === 'system' || g.kind === 'group').length;
  const inSummary = groups.filter((g) => g.kind === 'system' && g.inSummary).length;

  return (
    <>
      <ViewBar>
        <span>
          {bySystems
            ? `Систем: ${defined}${inSummary && inSummary !== defined ? `, в сводной спецификации: ${inSummary}` : ''}`
            : `Групп таблиц: ${defined}`}
          {groups.some((g) => g.kind === 'undefined' || g.kind === 'ungrouped') &&
            (bySystems ? ' · есть строки без системы' : ' · есть строки без группы')}
        </span>
        <LinkButton type="button" onClick={() => setOpen(new Set(groups.map((g) => g.key)))}>
          Развернуть все
        </LinkButton>
        <LinkButton type="button" onClick={() => setOpen(new Set())}>
          Свернуть все
        </LinkButton>
      </ViewBar>
      <Grid>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} className={c.num ? 'num' : c.wrap ? 'wrap' : undefined}>
                {c.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => {
            const isOpen = opened.has(g.key);
            const parts = g.sections.map((s) => `${s.label.split(' · ')[0]}: ${s.rows.length}`);
            return (
              <Fragment key={g.key}>
                <GroupTr>
                  <td colSpan={columns.length}>
                    <Toggle type="button" aria-expanded={isOpen} onClick={() => toggle(g.key)}>
                      {isOpen ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
                      {groupTitle(g)}
                    </Toggle>
                    <Facts>
                      {g.count} {rowsWord(g.count)}
                      {parts.length > 1 ? ` (${parts.join(', ')})` : ''}
                    </Facts>
                    {g.doubt > 0 && (
                      <Warn
                        role="img"
                        aria-label={`Строк с сомнением в системе: ${g.doubt}`}
                        title={`Строк с сомнением в системе: ${g.doubt} — подсказка у строки в графе «Система»`}
                      >
                        {WARN_SIGN} {g.doubt}
                      </Warn>
                    )}
                  </td>
                </GroupTr>
                {isOpen &&
                  g.sections.map((s) => (
                    <Fragment key={`${g.key}§${s.label}`}>
                      <SectionTr>
                        <td colSpan={columns.length}>{s.label}</td>
                      </SectionTr>
                      {s.rows.map((p) => (
                        <VolumeRow
                          key={p.id}
                          position={p}
                          columns={columns}
                          ctx={ctx}
                          target={p.id === targetId}
                        />
                      ))}
                    </Fragment>
                  ))}
              </Fragment>
            );
          })}
        </tbody>
      </Grid>
    </>
  );
}
